import type { IntlShape } from 'react-intl';
import { errors } from '../i18n/messages';
import { ApiError } from './client';

/** The verification desk's refusal codes (shared/src/node/verification/desk.ts `refuse`). */
const DESK_CODES = {
  gate_signed: true,
  no_snapshot: true,
  unknown_person: true,
  signer_is_author: true,
  package_not_in_phase: true,
  slice_exists: true,
  not_assignee: true,
  same_person: true,
  builder_cannot_verify: true,
  nothing_to_confirm: true,
  finding_closed: true,
  finding_taken: true,
  take_first: true,
  triage_first: true,
  rework_exists: true,
  duplicate_of_required: true,
  unknown_duplicate: true,
  not_a_signer: true,
  hat_not_required: true,
  gate_not_ready: true,
  reconfirm_required: true,
  docs_changed: true,
  report_changed: true,
  target_mismatch: true,
  environment_mismatch: true,
  rate_limited: true,
  unknown_package: true,
  unknown_attachment: true,
  origin_unknown: true,
  attachment_type: true,
  attachment_too_large: true,
  upload_failed: true,
} as const;
type DeskCode = keyof typeof DESK_CODES;

/**
 * The Build console's refusals (`api/routes/build.ts` `sendFailure`): the code says the class, the
 * `message` says what to do — "No package is ready to build: … AGR1 (278) — decide or re-confirm …",
 * "The plan changed since this proposal (…); propose the batch again." A console for maintainers, so
 * a caller that opts in (`serverMessage`) shows the sentence as the server wrote it rather than folded
 * into "Something went wrong (400)", which hid a reopened decision behind a config hunt on 2026-10-28.
 * Every other surface keeps the localized fallback: Chat's `conflict`s, say, are not operator text.
 */
const CONSOLE_REFUSALS = new Set(['bad_request', 'conflict']);

export interface ApiErrorContext {
  domain?: string;
  /** Show a `bad_request` / `conflict` body's `message` as written — the Build console's operator sentences. */
  serverMessage?: boolean;
}

/**
 * The API answers with codes (`{ error, requestId, … }`), never with sentences the UI shows; this
 * maps a code to the person's language. Unknown codes get the generic message with the status —
 * except, for a caller that opts in, a console refusal carrying its `message` (above).
 */
export function apiErrorMessage(intl: IntlShape, err: unknown, ctx: ApiErrorContext = {}): string {
  if (err instanceof ApiError) {
    const body = (err.body ?? {}) as { requestId?: string; message?: unknown };
    if (ctx.serverMessage && CONSOLE_REFUSALS.has(err.code) && typeof body.message === 'string' && body.message.trim()) return body.message.trim();
    switch (err.code) {
      case 'unauthenticated':
        return intl.formatMessage(errors.unauthenticated);
      case 'forbidden':
        return intl.formatMessage(errors.forbidden);
      case 'csrf':
        return intl.formatMessage(errors.csrf);
      case 'domain_refused':
        return intl.formatMessage(errors.domain_refused, { domain: ctx.domain ?? '' });
      case 'not_configured':
        return intl.formatMessage(errors.not_configured);
      case 'not_found':
        return intl.formatMessage(errors.not_found);
      case 'step_up_required':
        return intl.formatMessage(errors.step_up_required);
      case 'last_maintainer':
        return intl.formatMessage(errors.last_maintainer);
      case 'validation':
        return intl.formatMessage(errors.validation);
      case 'outcome_changed':
        return intl.formatMessage(errors.outcome_changed);
      case 'maintainers_only':
        return intl.formatMessage(errors.maintainers_only);
      case 'agenda_frozen':
        return intl.formatMessage(errors.agenda_frozen);
      case 'session_not_open':
        return intl.formatMessage(errors.session_not_open);
      case 'unknown_attendee':
        return intl.formatMessage(errors.unknown_attendee);
      case 'out_of_track':
        return intl.formatMessage(errors.out_of_track);
      case 'internal':
        return intl.formatMessage(errors.internal, { requestId: body.requestId ?? '—' });
      default:
        if (err.code in DESK_CODES) return intl.formatMessage(errors[err.code as DeskCode]);
        if (err.status === 404) return intl.formatMessage(errors.not_found);
        if (err.status === 409 && /^(cannot_|session_)/.test(err.code)) return intl.formatMessage(errors.session_state);
        return intl.formatMessage(errors.unexpected, { status: err.status });
    }
  }
  if (err instanceof TypeError) return intl.formatMessage(errors.network);
  return intl.formatMessage(errors.unexpected, { status: 0 });
}
